<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import { ElNotification } from 'element-plus';
import { Avatar, Coin, UserFilled } from '@element-plus/icons-vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import DigitalHumanRecharge from './components/DigitalHumanRecharge.vue';
import PointsRecharge from './components/PointsRecharge.vue';
import PartnerRecharge from './components/PartnerRecharge.vue';
import { scoreRechargeApi } from '@/api';
const { t } = useI18n();
// 获取路由对象
const route = useRoute();
// 充值类型切换
const activeTab = ref<string>('digital-human');
// 加载状态
const isLoading = ref<boolean>(true);

// 处理标签切换
const handleTabChange = async (tab: string) => {
  console.log('切换到标签:', tab);
  activeTab.value = tab;

  // 检查数据是否为空，如果为空则重新请求
  if (
    (tab === 'digital-human' && (!digitalData.value || digitalData.value.length === 0)) ||
    (tab === 'points' && (!scoreData.value || scoreData.value.length === 0))
  ) {
    console.log('数据为空，重新请求数据');
    try {
      const res = await scoreRechargeApi.reqGetScoreRecharge();
      console.log('重新获取数据成功:', res.data);
      if (res.statusCode === 0 && res.data) {
        digitalData.value = res.data.digitalMap || [];
        scoreData.value = res.data.scoreMap || [];
        enableBusiness.value = res.data.enableBusiness || false;
        currencysEnum.value = res.data.currencysEnum || 'CNY';
      }
    } catch (error) {
      console.error('重新获取数据失败', error);
      ElNotification.error({
        message: t('rechargeCenter.getRechargeDataFailed'),
        position: 'top-right',
      });
    }
  }
};

// 充值数据
const digitalData = ref<any[]>([]);
const scoreData = ref<any[]>([]);
const enableBusiness = ref<boolean>(false);
const currencysEnum = ref<string>('CNY');

// 窗口宽度，用于计算轮播图高度
const windowWidth = ref(window.innerWidth);
// 监听窗口大小变化
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};
// 支付相关状态
const paymentDialogVisible = ref<boolean>(false);
const currentRechargeInfo = ref<any>(null);
const currentRechargeAmount = ref<number>(0);
const currentDiscountCode = ref<string>('');

// 处理充值
const handleRecharge = (rechargeInfo: any) => {
  currentRechargeInfo.value = rechargeInfo;
  currentRechargeAmount.value = rechargeInfo?.amount || 0;
  currentDiscountCode.value = ''; // 重置折扣码
  paymentDialogVisible.value = true;
  console.log('充值信息11:', rechargeInfo);
};
// 监听enableBusiness变化，如果为false且当前是partner，则切换到digital-human
watch(
  () => enableBusiness.value,
  (newVal) => {
    if (!newVal && activeTab.value === 'partner') {
      activeTab.value = 'digital-human';
    }
  },
  { immediate: true }
);

onMounted(async () => {
  // 从URL参数获取初始选项卡
  const tabParam = route.query.tab as string;
  if (tabParam && ['digital-human', 'points', 'partner'].includes(tabParam)) {
    activeTab.value = tabParam;
  }
  await getRechargeData();
  // 添加窗口大小变化监听
  window.addEventListener('resize', handleResize);
  // 数据加载完成后设置加载状态为false
  isLoading.value = false;
});

onBeforeUnmount(() => {
  // 移除窗口大小变化监听
  window.removeEventListener('resize', handleResize);
});
const getRechargeData = async () => {
  try {
    const res = await scoreRechargeApi.reqGetScoreRecharge();
    console.log('初始数据加载:', res.data);
    if (res.statusCode === 0 && res.data) {
      digitalData.value = res.data.digitalMap || [];
      scoreData.value = res.data.scoreMap || [];
      enableBusiness.value = res.data.enableBusiness || false;
      currencysEnum.value = res.data.currencysEnum || 'CNY';

      // 检查数据是否正确加载
      console.log('数字人数据:', digitalData.value);
      console.log('积分数据:', scoreData.value);
    }
  } catch (error) {
    console.error('获取充值数据失败', error);
    ElNotification.error({
      message: t('rechargeCenter.getRechargeDataFailed'),
      position: 'top-right',
    });
  }
};
</script>
<template>
  <div class="w-[100vw] flex justify-center items-center">
    <div class="w-[85rem] py-[3rem]">
      <!-- 加载过渡效果 -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
      </div>

      <div v-else class="content-container">
        <!-- 顶部充值类型切换 -->
        <div class="tabs-wrapper flex flex-col items-center w-full justify-center">
          <h2 class="section-title">{{ $t('rechargeCenter.title') }}</h2>
          <div class="recharge-type-tabs">
            <div
              class="tab-item"
              :class="{ active: activeTab === 'digital-human' }"
              @click="handleTabChange('digital-human')"
            >
              <el-icon>
                <Avatar />
              </el-icon>
              <span>{{ $t('rechargeCenter.digitalHuman') }}</span>
            </div>
            <div
              class="tab-item"
              :class="{ active: activeTab === 'points' }"
              @click="handleTabChange('points')"
            >
              <el-icon>
                <Coin />
              </el-icon>
              <span>{{ $t('rechargeCenter.points') }}</span>
            </div>
            <div
              class="tab-item"
              :class="{ active: activeTab === 'partner' }"
              @click="handleTabChange('partner')"
              v-if="enableBusiness"
            >
              <el-icon>
                <UserFilled />
              </el-icon>
              <span>{{ $t('rechargeCenter.partner') }}</span>
            </div>
          </div>
        </div>

        <!-- 根据不同的tab显示不同的组件 -->
        <transition name="fade" mode="out-in">
          <div class="recharge-component-wrapper" :key="activeTab">
            <DigitalHumanRecharge
              v-if="activeTab === 'digital-human'"
              :digital-data="digitalData"
              :currency="currencysEnum"
              @recharge="handleRecharge"
            >
            </DigitalHumanRecharge>

            <PointsRecharge
              v-if="activeTab === 'points'"
              :digital-data="digitalData"
              :score-data="scoreData"
              :currency="currencysEnum"
              @recharge="handleRecharge"
            >
            </PointsRecharge>

            <PartnerRecharge
              v-if="activeTab === 'partner'"
              :digital-data="digitalData"
              :score-data="scoreData"
              :currency="currencysEnum"
              @recharge="handleRecharge"
            >
            </PartnerRecharge>
          </div>
        </transition>

        <div class="recharge-notice">
          <h3>{{ $t('rechargeCenter.purchaseNotice.title') }}</h3>
          <ol>
            <li>{{ $t('rechargeCenter.purchaseNotice.item1') }}</li>
            <li>{{ $t('rechargeCenter.purchaseNotice.item2') }}</li>
            <li>{{ $t('rechargeCenter.purchaseNotice.item3') }}</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
/* 加载动画样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--el-color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 内容区域过渡效果 */
.content-container {
  animation: fadeIn 0.5s ease-in;
  width: 100%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* 过渡效果 */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* 轮播图样式 - 参考BannerSection.vue */
.banner-section {
  margin: 0 auto 15px auto;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.banner-section:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transform: translateY(-5px);
}

.carousel-container {
  width: 100%;
}

.banner-item {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  overflow: hidden;
}

.banner-link {
  display: block;
  width: 100%;
  height: 100%;
}

.banner-item h2 {
  font-size: 28px;
  margin-bottom: 15px;
}

.banner-item p {
  font-size: 16px;
  margin-bottom: 20px;
  max-width: 80%;
}

.banner-btn {
  font-weight: bold;
}

.banner-image {
  width: 100%;
  height: 100%;
}

@media (max-width: 768px) {
  .banner-item h2 {
    font-size: 22px;
  }

  .banner-item p {
    font-size: 14px;
    max-width: 95%;
  }
}

/* 顶部充值类型样式 */
.tabs-wrapper {
  margin: 30px auto 20px;
  max-width: 1350px;
  padding: 0;
}

.section-title {
  font-size: 3rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 25px;
  padding-left: 10px;
}

.recharge-type-tabs {
  display: flex;
  width: 60%;
  max-width: 600px;
  margin: 0 0 30px 0;
  background-color: #f5f5f5;
  border-radius: 30px;
  padding: 5px;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.tab-item:hover {
  transform: translateY(-2px);
}

.tab-item:active {
  transform: scale(0.95);
}

.tab-item.active {
  background-color: var(--el-color-primary);
  color: #fff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  font-weight: bold;
}

.tab-item .el-icon {
  font-size: 18px;
}

.recharge-notice {
  background-color: #f8f8f8;
  padding: 15px;
  border-radius: 4px;
  margin-top: 1rem;
}

.recharge-notice h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
}

.recharge-notice ol {
  margin: 0;
  padding-left: 20px;
}

.recharge-notice li {
  margin: 5px 0;
  color: #606266;
}
</style>
